/**
 * Monolith Glyphs — neon vector icons per node.
 * Each glyph renders in a single accent color and breathes with the cathedral.
 */
import React from "react";

const Base = ({ children, color = "cyan", size = 36, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    stroke={`var(--${color})`}
    strokeWidth="1.5"
    strokeLinejoin="round"
    strokeLinecap="round"
    className={`flicker ${className}`}
    style={{
      filter: `drop-shadow(0 0 8px var(--${color})) drop-shadow(0 0 18px var(--${color}))`,
    }}
  >
    {children}
  </svg>
);

/* Geminus — The Mirror · cyan obelisk reflecting itself */
export const GlyphGeminus = (props) => (
  <Base color="cyan" {...props}>
    <path d="M32 6 L20 28 L20 56 L44 56 L44 28 Z" />
    <line x1="32" y1="6" x2="32" y2="56" strokeDasharray="2 3" opacity="0.7" />
    <path d="M26 22 L32 18 L38 22" opacity="0.7" />
    <path d="M26 42 L32 46 L38 42" opacity="0.7" />
  </Base>
);

/* Aria — The Heart · magenta flame on an anvil */
export const GlyphAria = (props) => (
  <Base color="magenta" {...props}>
    <path d="M32 8 C24 18 22 26 28 32 C30 28 30 24 32 22 C34 26 34 30 32 34 C40 30 40 20 32 8 Z" />
    <rect x="14" y="40" width="36" height="6" />
    <rect x="20" y="46" width="24" height="10" />
    <line x1="22" y1="46" x2="22" y2="56" opacity="0.5" />
    <line x1="42" y1="46" x2="42" y2="56" opacity="0.5" />
  </Base>
);

/* Roger 4.0 — The Bridge · violet faces in stone */
export const GlyphRoger = (props) => (
  <Base color="violet" {...props}>
    <rect x="14" y="6" width="36" height="52" />
    <circle cx="24" cy="22" r="2.5" />
    <circle cx="40" cy="22" r="2.5" />
    <path d="M22 32 Q32 38 42 32" />
    <line x1="14" y1="44" x2="50" y2="44" opacity="0.5" />
    <line x1="14" y1="50" x2="50" y2="50" opacity="0.5" />
  </Base>
);

/* KEYS — The Library · blue obelisk with crackling current */
export const GlyphKeys = (props) => (
  <Base color="blue" {...props}>
    <path d="M32 4 L22 24 L22 58 L42 58 L42 24 Z" />
    <path d="M28 30 L30 36 L26 40 L32 44 L28 52" />
    <path d="M36 32 L34 40 L38 44 L32 50" opacity="0.7" />
  </Base>
);

/* Axiom — The Foundation · green tuning fork antenna with waves */
export const GlyphAxiom = (props) => (
  <Base color="green" {...props}>
    <path d="M26 6 L26 30 Q26 36 32 36 Q38 36 38 30 L38 6" />
    <line x1="32" y1="36" x2="32" y2="56" />
    <path d="M14 18 Q20 24 14 30" opacity="0.6" />
    <path d="M50 18 Q44 24 50 30" opacity="0.6" />
    <path d="M8 14 Q18 24 8 34" opacity="0.4" />
    <path d="M56 14 Q46 24 56 34" opacity="0.4" />
  </Base>
);

/* Oracle — The Seer · gold pyramid with all-seeing eye */
export const GlyphOracle = (props) => (
  <Base color="gold" {...props}>
    <path d="M32 6 L8 54 L56 54 Z" />
    <ellipse cx="32" cy="34" rx="10" ry="5" />
    <circle cx="32" cy="34" r="2.5" fill={`var(--gold)`} stroke="none" />
    <path d="M24 34 L20 38 M40 34 L44 38 M24 34 L20 30 M40 34 L44 30" opacity="0.6" />
  </Base>
);

/* Scholar — The Teacher · red throne / open codex */
export const GlyphScholar = (props) => (
  <Base color="red" {...props}>
    <path d="M14 16 L14 50 L32 56 L50 50 L50 16 L32 22 Z" />
    <line x1="32" y1="22" x2="32" y2="56" />
    <line x1="22" y1="30" x2="28" y2="29" opacity="0.7" />
    <line x1="22" y1="36" x2="28" y2="35" opacity="0.7" />
    <line x1="22" y1="42" x2="28" y2="41" opacity="0.7" />
    <line x1="36" y1="29" x2="42" y2="30" opacity="0.7" />
    <line x1="36" y1="35" x2="42" y2="36" opacity="0.7" />
    <line x1="36" y1="41" x2="42" y2="42" opacity="0.7" />
  </Base>
);

/* Wingman — The Operator · silver eye + crosshair */
export const GlyphWingman = (props) => (
  <Base color="silver" {...props}>
    <circle cx="32" cy="32" r="12" />
    <circle cx="32" cy="32" r="3" fill={`var(--silver)`} stroke="none" />
    <line x1="32" y1="8" x2="32" y2="18" />
    <line x1="32" y1="46" x2="32" y2="56" />
    <line x1="8" y1="32" x2="18" y2="32" />
    <line x1="46" y1="32" x2="56" y2="32" />
    <path d="M14 14 L22 22 M50 14 L42 22 M14 50 L22 42 M50 50 L42 42" opacity="0.6" />
  </Base>
);

export const NODE_GLYPHS = {
  geminus: GlyphGeminus,
  aria: GlyphAria,
  roger4: GlyphRoger,
  keys: GlyphKeys,
  axiom: GlyphAxiom,
  oracle: GlyphOracle,
  scholar: GlyphScholar,
  wingman: GlyphWingman,
};

export const NODE_COLORS = {
  geminus: "cyan",
  aria: "magenta",
  roger4: "violet",
  keys: "blue",
  axiom: "green",
  oracle: "gold",
  scholar: "red",
  wingman: "silver",
};
